import React, { useState } from "react";
import { PageLayout } from "../../Page.js";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import BasicInformation from "./BasicInformation.js";
import OnboardingScreens from "./OnboardingScreens.js";
import DisplayConfiguration from "./DisplayConfiguration.js";
import SelectionCriteria from "./SelectionCriteria.js";
import { getCompletionStatus, isCompleted } from "../utils/completionValidation.js";
import { useAddSpendingGoal, useUpdateSpendingGoal, useFetchSpendingGoals } from "../hooks/useSpendingGoals.js";

function SpendingGoalsTable() {
  const { data: spendingGoals, refetch: refetchSpendingGoals } = useFetchSpendingGoals();
  const addSpendingGoalMutation = useAddSpendingGoal();
  const updateSpendingGoalMutation = useUpdateSpendingGoal();
  const [isNew, setIsNew] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(isNew);
  const [editingGoal, setEditingGoal] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    iconUrl: "",
    isActive: true,
    spendingGoalKey: "",
    order: "",
    faq: "",
    displayConfiguration: JSON.stringify({
      onboardingScreens: []
    }, null, 2),
    selectionCriteria: JSON.stringify({}, null, 2)
  });
  const [jsonErrors, setJsonErrors] = useState({
    displayConfiguration: false,
    selectionCriteria: false
  });
  const [onboardingScreens, setOnboardingScreens] = useState([]);
  const [completionStatus, setCompletionStatus] = useState({
    basicInformation: false,
    onboardingScreens: false,
    displayConfiguration: false,
    selectionCriteria: false
  });

  const handleEditClick = (spendingGoal) => {
    setEditingGoal(spendingGoal);
    const displayConfig = spendingGoal.displayConfiguration || {};
    const initialFormData = {
      title: spendingGoal.title || "",
      iconUrl: spendingGoal.iconUrl || "",
      isActive: spendingGoal.isActive !== undefined ? spendingGoal.isActive : true,
      spendingGoalKey: spendingGoal.spendingGoalKey || "",
      order: spendingGoal.order !== null && spendingGoal.order !== undefined ? spendingGoal.order : "",
      faq: spendingGoal.faq || "",
      displayConfiguration: JSON.stringify(displayConfig, null, 2),
      selectionCriteria: JSON.stringify(spendingGoal.selectionCriteria || {}, null, 2)
    };

    setFormData(initialFormData);

    // Extract onboarding screens from display configuration
    const screens = displayConfig.onboardingScreens || [];
    setOnboardingScreens(screens);

    setJsonErrors({
      displayConfiguration: false,
      selectionCriteria: false
    });

    const newCompletionStatus = getCompletionStatus(initialFormData, screens);
    setCompletionStatus(newCompletionStatus);

    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsNew(false);
    setEditModalOpen(false);
    setEditingGoal(null);
    setFormData({
      title: "",
      iconUrl: "",
      isActive: true,
      spendingGoalKey: "",
      order: "",
      faq: "",
      displayConfiguration: JSON.stringify({
        onboardingScreens: []
      }, null, 2),
      selectionCriteria: JSON.stringify({}, null, 2)
    });
    setOnboardingScreens([]);
    setJsonErrors({
      displayConfiguration: false,
      selectionCriteria: false
    });
  };

  const updateCompletionStatus = (newFormData, newOnboardingScreens) => {
    const newCompletionStatus = getCompletionStatus(newFormData, newOnboardingScreens);
    setCompletionStatus(newCompletionStatus);
  };

  const handleFormChange = (field, value) => {
    const newFormData = {
      ...formData,
      [field]: value
    };

    setFormData(newFormData);

    // Clear JSON errors when user starts typing
    if (field === "displayConfiguration" || field === "selectionCriteria") {
      setJsonErrors((prev) => ({
        ...prev,
        [field]: false
      }));
    }

    // Update completion status after form change
    updateCompletionStatus(newFormData, onboardingScreens);
  };

  const handleOnboardingScreenChange = (index, field, value) => {
    const updatedScreens = [...onboardingScreens];
    updatedScreens[index] = {
      ...updatedScreens[index],
      [field]: value
    };
    setOnboardingScreens(updatedScreens);

    // Update the displayConfiguration JSON
    try {
      const displayConfig = JSON.parse(formData.displayConfiguration);
      displayConfig.onboardingScreens = updatedScreens;
      setFormData((prev) => ({
        ...prev,
        displayConfiguration: JSON.stringify(displayConfig, null, 2)
      }));
    } catch (error) {
      setJsonErrors((prev) => ({
        ...prev,
        displayConfiguration: true
      }));
    }

    // Update completion status after screen change
    updateCompletionStatus(formData, updatedScreens);
  };

  const addOnboardingScreen = () => {
    const newScreen = {
      identifier: "InfoStepScreen",
      id: `screen_${Date.now()}`,
      title: "",
      subtitle: "",
      body: "",
      iconUrl: ""
    };
    const updatedScreens = [...onboardingScreens, newScreen];
    setOnboardingScreens(updatedScreens);

    // Update the displayConfiguration JSON
    try {
      const displayConfig = JSON.parse(formData.displayConfiguration);
      displayConfig.onboardingScreens = updatedScreens;
      setFormData((prev) => ({
        ...prev,
        displayConfiguration: JSON.stringify(displayConfig, null, 2)
      }));
    } catch (error) {
      // console.warn("Could not update displayConfiguration JSON:", error);
    }

    // Update completion status after adding screen
    updateCompletionStatus(formData, updatedScreens);
  };

  const removeOnboardingScreen = (index) => {
    const updatedScreens = onboardingScreens.filter((_, i) => i !== index);
    setOnboardingScreens(updatedScreens);

    // Update the displayConfiguration JSON
    try {
      const displayConfig = JSON.parse(formData.displayConfiguration);
      displayConfig.onboardingScreens = updatedScreens;
      setFormData((prev) => ({
        ...prev,
        displayConfiguration: JSON.stringify(displayConfig, null, 2)
      }));
    } catch (error) {
      // console.warn("Could not update displayConfiguration JSON:", error);
    }

    // Update completion status after removing screen
    updateCompletionStatus(formData, updatedScreens);
  };

  const validateJson = (jsonString, field) => {
    try {
      JSON.parse(jsonString);
      setJsonErrors((prev) => ({
        ...prev,
        [field]: false
      }));
      return true;
    } catch (error) {
      setJsonErrors((prev) => ({
        ...prev,
        [field]: true
      }));
      return false;
    }
  };

  const handleSave = () => {
    // Validate JSON fields
    const isDisplayConfigValid = validateJson(formData.displayConfiguration, "displayConfiguration");
    const isSelectionCriteriaValid = validateJson(formData.selectionCriteria, "selectionCriteria");

    if (!isDisplayConfigValid || !isSelectionCriteriaValid) {
      return;
    }

    const updateData = {
      title: formData.title,
      iconUrl: formData.iconUrl,
      isActive: formData.isActive,
      spendingGoalKey: formData.spendingGoalKey,
      order: formData.order === "" ? null : parseInt(formData.order, 10),
      faq: formData.faq,
      displayConfiguration: JSON.parse(formData.displayConfiguration),
      selectionCriteria: JSON.parse(formData.selectionCriteria)
    };

    if (isNew) {
      addSpendingGoalMutation.mutate(formData);
      setIsNew(false);
      refetchSpendingGoals();
      return;
    }

    updateSpendingGoalMutation.mutate({
      id: editingGoal.id,
      ...updateData
    }, {
      onSuccess: () => {
        handleCloseEditModal();
        refetchSpendingGoals();
      }
    });
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 1000,
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
    maxHeight: "90vh",
    overflowY: "auto"
  };

  return (
    <PageLayout>
      <Button onClick={() => {
        setIsNew(true);
        setEditModalOpen(true);
      }}>Add Personal Challenge</Button>
      {/* create spending goal crud table */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Icon</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {spendingGoals?.spendingGoals?.map((spendingGoal) => (
              <TableRow key={spendingGoal.id}>
                <TableCell>{spendingGoal.title}</TableCell>
                <TableCell>
                  <img src={spendingGoal.iconUrl} alt="Spending Goal Icon" style={{ width: "50px", height: "50px" }} />
                </TableCell>
                <TableCell>{spendingGoal.isActive ? "Active" : "Inactive"}</TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleEditClick(spendingGoal)}
                  >
                    Edit
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Modal */}
      <Modal open={editModalOpen} onClose={handleCloseEditModal}>
        <Box sx={modalStyle}>
          <Typography variant="h6" component="h2" gutterBottom>
            Edit Personal Challenge
          </Typography>

          {/* Basic Information */}
          <BasicInformation
            formData={formData}
            onFormChange={handleFormChange}
            isComplete={completionStatus.basicInformation}
          />

          {/* Onboarding Screens */}
          <OnboardingScreens
            onboardingScreens={onboardingScreens}
            onScreenChange={handleOnboardingScreenChange}
            onAddScreen={addOnboardingScreen}
            onRemoveScreen={removeOnboardingScreen}
            isComplete={completionStatus.onboardingScreens}
          />

          {/* Display Configuration */}
          <DisplayConfiguration
            formData={formData}
            onFormChange={handleFormChange}
            jsonErrors={jsonErrors}
            isComplete={completionStatus.displayConfiguration}
          />

          {/* Selection Criteria */}
          <SelectionCriteria
            formData={formData}
            onFormChange={handleFormChange}
            jsonErrors={jsonErrors}
            isComplete={completionStatus.selectionCriteria}
            onSelectionCriteriaChange={(jsonString) => handleFormChange("selectionCriteria", jsonString)}
          />

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => handleFormChange("isActive", e.target.checked)}
                />
              }
              label="Active"
            />
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleCloseEditModal}
                disabled={updateSpendingGoalMutation.isLoading}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={
                  updateSpendingGoalMutation.isLoading ||
                  !formData.title.trim() ||
                  jsonErrors.displayConfiguration ||
                  jsonErrors.selectionCriteria
                }
                color={isCompleted(completionStatus) ? "success" : "primary"}
              >
                {updateSpendingGoalMutation.isLoading
                  ? "Saving..."
                  : isCompleted(completionStatus)
                    ? "Save & Publish"
                    : "Save"
                }
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </PageLayout>
  );
}

export default SpendingGoalsTable;
